import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import type { GenericOpportunityActivityDataType } from '@/api/attribution';
import type { OpportunityStatusOldType } from '@/api/opportunity';
import { RangeFilter } from '@/components/filters';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { Link } from '@/components/typography';
import type SolAPIError from '@/error/SolAPIError';
import { CampaignFilter, VendorFilter } from '@/features/filters';
import { numberFormat } from '@/helper/numberFormatter';
import { useCustomImpactScoresEnabled } from '@/hooks/useCustomImpactScoresEnabled';
import { ROUTES } from '@/router';

type Props = {
  oppType: OpportunityStatusOldType;
  breakdownObject: 'vendor' | 'campaign';
  data: GenericOpportunityActivityDataType[] | undefined;
  totalResults: number | undefined;
  isLoading: boolean;
  error: SolAPIError | null;
};

const OpportunityAttributionOnlineTable = ({
  oppType,
  breakdownObject,
  data,
  totalResults,
  isLoading,
  error,
}: Props) => {
  const breakdownColumnId = breakdownObject === 'vendor' ? 'vendorId' : 'campaignId';
  const isCustomImpactScoresEnabled = useCustomImpactScoresEnabled();

  const columns = useMemo<
    InstantSearchDataTableColumnDef<GenericOpportunityActivityDataType>[]
  >(() => {
    const customAttributionAccessorKey =
      oppType === 'pipeline'
        ? 'opportunityInfluencedSumPipelineCustom'
        : 'opportunityInfluencedSumClosedWonCustom';

    return [
      {
        header: breakdownObject === 'vendor' ? t`Vendor` : t`Campaign`,
        id: breakdownColumnId,
        accessorKey: 'id',
        size: 240,
        minSize: 160,
        flex: 2,
        enableSorting: true,
        filterComponent: (props) =>
          breakdownObject === 'vendor' ? (
            <VendorFilter {...props} />
          ) : (
            <CampaignFilter {...props} />
          ),
        cell: ({ row }) => {
          const field = row.original;
          return (
            <Link
              variant="caption1"
              to={
                breakdownObject === 'vendor'
                  ? ROUTES.vendorById(field.id)
                  : ROUTES.campaignById(field.id)
              }
            >
              {field.name}
            </Link>
          );
        },
      },
      {
        header: t`Visits`,
        id: 'audienceVisits',
        accessorKey: 'audienceVisits',
        minSize: 100,
        flex: 1,
        meta: { align: 'end' },
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => numberFormat(row.original.audienceVisits),
      },
      {
        header: t`Pipeline Influence`,
        id: 'pipelineInfluenced',
        accessorKey: 'pipelineInfluenced',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'closedWon',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineInfluenced;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams(
                breakdownObject === 'vendor'
                  ? { 'o.vendorIdListPipelineInfluence': field.id }
                  : { 'o.campaignIdListPipelineInfluence': field.id },
              )}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`Closed Won Influence`,
        id: 'closedWonInfluenced',
        accessorKey: 'closedWonInfluenced',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'pipeline',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.closedWonInfluenced;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams(
                breakdownObject === 'vendor'
                  ? { 'o.vendorIdListClosedwonInfluence': field.id }
                  : { 'o.campaignIdListClosedwonInfluence': field.id },
              )}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`First-Touch Attribution`,
        id:
          oppType === 'pipeline'
            ? 'pipelineFirstTouchAttribution'
            : 'closedWonFirstTouchAttribution',
        accessorKey:
          oppType === 'pipeline'
            ? 'pipelineFirstTouchAttribution'
            : 'closedWonFirstTouchAttribution',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text =
            oppType === 'pipeline'
              ? field.pipelineFirstTouchAttribution
              : field.closedWonFirstTouchAttribution;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams(
                oppType === 'pipeline'
                  ? breakdownObject === 'vendor'
                    ? { 'o.vendorIdListPipelineFirstTouch': field.id }
                    : { 'o.campaignIdListPipelineFirstTouch': field.id }
                  : breakdownObject === 'vendor'
                    ? { 'o.vendorIdListClosedwonFirstTouch': field.id }
                    : { 'o.campaignIdListClosedwonFirstTouch': field.id },
              )}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`Last-Touch Attribution`,
        id: 'pipelineLastTouchAttribution',
        accessorKey: 'pipelineLastTouchAttribution',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: oppType === 'closedWon',
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineLastTouchAttribution;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams(
                breakdownObject === 'vendor'
                  ? { 'o.vendorIdListPipelineLastTouch': field.id }
                  : { 'o.campaignIdListPipelineLastTouch': field.id },
              )}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: t`Linear Attribution`,
        id: oppType === 'pipeline' ? 'pipelineLinearAttribution' : 'closedWonLinearAttribution',
        accessorKey:
          oppType === 'pipeline' ? 'pipelineLinearAttribution' : 'closedWonLinearAttribution',
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text =
            oppType === 'pipeline'
              ? field.pipelineLinearAttribution
              : field.closedWonLinearAttribution;
          return text === 0 ? (
            numberFormat(text, { isCurrency: true, precision: 0 })
          ) : (
            <Link
              variant="caption1"
              to={ROUTES.opportunityActivityWithParams(
                oppType === 'pipeline'
                  ? breakdownObject === 'vendor'
                    ? { 'o.vendorIdListPipelineLinear': field.id }
                    : { 'o.campaignIdListPipelineLinear': field.id }
                  : breakdownObject === 'vendor'
                    ? { 'o.vendorIdListClosedwonLinear': field.id }
                    : { 'o.campaignIdListClosedwonLinear': field.id },
              )}
            >
              {numberFormat(text, { isCurrency: true, precision: 0 })}
            </Link>
          );
        },
      },
      {
        header: oppType === 'pipeline' ? t`Impact Score Pipeline` : t`Impact Score Closed Won`,
        id: customAttributionAccessorKey,
        accessorKey: customAttributionAccessorKey,
        minSize: 120,
        flex: 1,
        meta: { align: 'end' },
        hidden: !isCustomImpactScoresEnabled,
        enableSorting: true,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const text = row.original[customAttributionAccessorKey];
          return numberFormat(text, { isCurrency: true, precision: 0 });
        },
      },
    ];
  }, [breakdownColumnId, breakdownObject, isCustomImpactScoresEnabled, oppType]);

  return (
    <div data-testid="attribution-table">
      <InstantSearchDataTable<GenericOpportunityActivityDataType>
        columns={columns}
        data={data}
        totalResults={totalResults}
        loading={isLoading}
        emptyMessage={
          breakdownObject === 'vendor' ? (
            <Trans>No vendors found.</Trans>
          ) : (
            <Trans>No campaigns found.</Trans>
          )
        }
        error={error}
        getRowId={(record) => record.id}
        initialColumnPinning={{
          start: [breakdownColumnId],
        }}
      />
    </div>
  );
};

export default OpportunityAttributionOnlineTable;
